import React, { useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { noop } from 'ui-components/default-props/noop';

import { SellerSettings } from '../../../enums/seller-settings/seller-settings';
import { LogService } from '../../../services/log-service';
import { updateSellerSettingsBulk } from '../../../pages/settings/api/save-settings';
import { GenericErrorRetryContainer } from '../../../containers/generic-error-retry-container';
import { ModalPortal } from '../../../components/modal-portal';
import { useServiceMappingsSetupCompleteSellerSetting } from '../../../hooks/use-seller-setting';

import * as Api from '../../../store/map-requested-service/map-requested-service.api';
import * as Actions from '../../../store/map-requested-service/map-requested-service.actions';
import * as SellerActions from '../../../store/seller/seller.actions';
import { messages } from './messages';

/**
 * @param {object} props
 * @param {(args: { onSave: (requestedServiceMapping: object, sellerLabelProviderId: number) => void, isSaving: boolean }) => React.ReactNode} props.children
 * @param {(successData: { serviceCollectionGuid: string, serviceId: number, sellerLabelProviderId: number, packageTypeId: number }) => void} [props.onSuccess]
 * @returns {React.ReactElement}
 */
export const SaveRequestedService = ({ children, onSuccess = noop }) => {
  const dispatch = useDispatch();
  const [isSaving, setIsSaving] = useAsyncSafeState(false);
  const [didError, setDidError] = useAsyncSafeState(false);
  const isServiceMappingsSetup = useServiceMappingsSetupCompleteSellerSetting();

  const onSave = useCallback(
    (requestedServiceMapping, sellerLabelProviderId) => {
      setIsSaving(true);
      setDidError(false);
      Api.mapRequestedService(requestedServiceMapping)
        .then(() => {
          onSuccess({
            serviceId: requestedServiceMapping.serviceId,
            serviceCollectionGuid: requestedServiceMapping.serviceCollectionGuid,
            sellerLabelProviderId,
            packageTypeId: requestedServiceMapping.packageTypeId,
          });
          dispatch(SellerActions.clearOutServiceMappings());
          dispatch(
            Actions.mapRequestedServiceSuccess({
              storeGuid: requestedServiceMapping.storeGuid,
            }),
          );
          if (!isServiceMappingsSetup) {
            updateSellerSettingsBulk({ [SellerSettings.isServiceMappingsSetupComplete.key]: 'true' })
              .then((newSettings) => dispatch(SellerActions.saveNewSellerSettings(newSettings)))
              .catch((e) => LogService.logError(new Error('Failed to save service mappings setup setting', { cause: e })));
          }
        })
        .catch(() => {
          setDidError(true);
        })
        .finally(() => {
          setIsSaving(false);
        });
    },
    [dispatch, isServiceMappingsSetup, onSuccess, setDidError, setIsSaving],
  );

  const errorModalCloseShim = useMemo(() => {
    return {
      closeModal: () => setDidError(false),
    };
  }, [setDidError]);
  const onCloseModal = useCallback(() => setDidError(false), [setDidError]);

  return (
    <>
      {children({ onSave, isSaving })}
      {didError && (
        <ModalPortal onClose={onCloseModal}>
          <GenericErrorRetryContainer
            message={<FormattedMessage {...messages.mapRequestedServiceErrorMessage} />}
            modalFrameworkMethods={errorModalCloseShim}
            onClick={onSave}
            title={<FormattedMessage {...messages.mapRequestedServiceErrorTitle} />}
          />
        </ModalPortal>
      )}
    </>
  );
};
